<template>
  <div v-if="model.IdOperacion" class="chat-layout px-4 row">
    <div class="chat-main col-xl-10 col-lg-9 col-md-8">
      <!-- Encabezado del chat -->
      <div class="chat-header">
        <h1>{{ $t("chatOperacion.titulo") }} {{ props.codigoSecuencialOperacion }}</h1>
      </div>
      <!-- Conversación del chat -->
      <div class="chat-messages">
        <div
          v-for="(mensaje, index) in model.Comunicaciones"
          class="message"
          :class="{
            'user-message': props.esBackoffice ? mensaje.EsComunicacionSistema : !mensaje.EsComunicacionSistema,
            'reply-message': props.esBackoffice ? !mensaje.EsComunicacionSistema : mensaje.EsComunicacionSistema,
          }"
          :id="index === model.Comunicaciones.length - 1 ? 'last-message' : `$index-message-${index}`"
        >
          <div v-if="mensaje.EsComunicacionSistema && !props.esBackoffice" class="circle-icon-system">
            <i class="fas fa-user-headset"></i>
          </div>
          <div v-if="!mensaje.EsComunicacionSistema && props.esBackoffice" class="circle-icon">
            <i class="fas fa-user"></i>
          </div>
          <div class="message-content">
            <div class="message-top texto-light">{{ mensaje.Creador }}:</div>
            <p>
              {{ mensaje.Contenido }}
            </p>
            <div class="message-attachments">
              <div v-for="adjunto in mensaje.RutasAdjuntos" class="d-flex align-items-center documento gap-2" @click="descargaAdjunto(adjunto)">
                <i class="fa fa-paperclip"></i>
                <span> {{ getFilename(adjunto) }} </span>
              </div>
            </div>
            <div class="message-bottom">
              <span class="texto-light">{{ new Date(mensaje.FechaCreacion).toLocaleString("es-ES") }}</span>
              <div class="message-status" v-if="props.esBackoffice ? mensaje.EsComunicacionSistema : !mensaje.EsComunicacionSistema">
                <i v-if="mensaje.LeidoPorRolContrario" class="fa-solid fa-check-double check-double"></i>
                <i v-else class="fa-regular fa-check check-single"></i>
              </div>
            </div>
          </div>
          <div v-if="mensaje.EsComunicacionSistema && props.esBackoffice" class="circle-icon-system">
            <i class="fas fa-user-headset"></i>
          </div>
          <div v-if="!mensaje.EsComunicacionSistema && !props.esBackoffice" class="circle-icon">
            <i class="fas fa-user"></i>
          </div>
        </div>
      </div>

      <!-- Área de respuesta -->
      <div
        v-if="puedeEscribirNuevoMensaje"
        :class="['chat-reply', { 'con-adjuntos': Object.values(ficheros).filter((fichero) => fichero.file !== null && fichero.file !== undefined).length > 0 }]"
      >
        <div class="reply-attach-file">
          <MultipleFileUploader
            :max-files="5"
            v-model:ficheros="ficheros"
            :textForAddAttachmentLabel="''"
            :textForAddAttachmentButton="''"
            :textForNonDownloadableFiles="''"
            mode="clip"
          >
          </MultipleFileUploader>
        </div>
        <div class="reply-input">
          <textarea v-model="textoMensaje" :placeholder="$t('chatOperacion.mensaje.placeholder')" rows="3" />
          <button class="send-button" @click="sendMessage">➤</button>
        </div>
      </div>
      <div v-else class="chat-reply">
        <div class="reply-input">
          <textarea v-model="textoMensaje" rows="3" disabled :placeholder="textoNoPuedeEscribirMensaje(esBackoffice)" />
        </div>
      </div>
    </div>

    <!-- Panel lateral derecho -->
    <div class="chat-sidebar col-xl-2 col-lg-3 col-md-4 mt-sm-3 mt-0">
      <div class="sidebar-box">
        <h6>{{ $t("chatOperacion.responsable.titulo") }}</h6>
        <div v-if="detalleOperacion?.UserNameResponsable">{{ detalleOperacion?.NombreCompletoResponsable }}</div>
        <div v-else>{{ $t("ticket.detalle.no_responsable") }}</div>
        <DxButton
          v-bind="secondaryButtonProps()"
          :visible="showAutoAsignarResponsable"
          :text="$t('chatOperacion.responsable.autoasignar_responsable')"
          :class="'w-100 mt-3'"
          @click="autoAsignarResponsable"
        />
        <DxButton
          v-bind="secondaryButtonProps()"
          :visible="showAsignarResponsable"
          :text="$t('chatOperacion.responsable.asignar_responsable')"
          :class="'w-100 mt-3'"
          @click="asignarResponsable"
        />
      </div>

      <!-- Caja para ir al detalle de la operación -->
      <div class="sidebar-box">
        <h6>{{ $t("chatOperacion.detalle.titulo") }}</h6>
        <DxButton
          v-bind="{ ...primaryButtonProps(), icon: 'fa-solid fa-magnifying-glass fa-sm' }"
          :text="$t('chatOperacion.detalle.ver_detalle')"
          :class="'w-100 mt-3'"
          @click="irADetalleOperacion"
        />
      </div>

      <!-- Caja para listar adjuntos -->
      <div class="sidebar-box">
        <h6>{{ $t("chatOperacion.adjuntos.titulo") }}</h6>
        <AdjuntosList :adjuntos="todosLosAdjuntos" :texto-no-adjuntos="$t('chatOperacion.adjuntos.no_hay_adjuntos')" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type ComunicacionesOperacionModel, type CreateComunicacionOperacionModel, type DetailOperacionAguaModel } from "@api/api";
import { useApi } from "@api/integration";
import { computed, nextTick, onBeforeMount, onMounted, onUnmounted, reactive, ref, watch, type PropType } from "vue";
import { useAuthorization } from "@/.shared/services/auth/authorization";
import { PermissionsApp } from "@/.shared/services/auth/authorization/authorization-service-impl";
import MultipleFileUploader from "@/.shared/components/file-uploading/MultipleFileUploader.vue";
import { useIntl } from "@services/i18n";
import FileSaver from "file-saver";
import { useModalService } from "@/.shared/services/modal-service";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { DxButton } from "devextreme-vue/button";
import { primaryButtonProps, secondaryButtonProps } from "@/.shared/helpers/button-helper";
import { getDocumentalFileName as getFilename } from "@/.shared/helpers/documental-file-name-helper";
import { type DetailOperacionEnergiaModel } from "@api/api";
import { useAuthentication } from "@services/auth";
import { messageBoxResult } from "@components/dialogs/message-box";
import AdjuntosList from "@/.shared/components/AdjuntosList.vue";
import InputSelectBox, { type InputSelectBoxModalResult } from "../dialogs/InputSelectBox.vue";

const props = defineProps({
  idOperacion: {
    type: String,
    required: true,
  },
  esBackoffice: {
    type: Boolean,
    required: true,
  },
  codigoSecuencialOperacion: {
    type: String,
    required: true,
  },
  tipo: {
    type: String as PropType<"ENERGIA" | "AGUA">,
    required: true,
  },
});

const emit = defineEmits(["detalle-operacion"]);

const model = reactive<ComunicacionesOperacionModel>({} as ComunicacionesOperacionModel);
const api = useApi();
const auth = useAuthentication();
const autho = useAuthorization();
const isInitialLoad = reactive({ value: true });
const ficheros = ref<Ficheros>({});
const textoMensaje = ref<string>("");
const intl = useIntl();
const modal = useModalService();
const detalleOperacion = ref<DetailOperacionEnergiaModel | DetailOperacionAguaModel>();

onBeforeMount(async () => {
  await loadDetalleOperacion();
});

onMounted(async () => {
  await api.callStandardApi("getComunicacionesOperacionById", { showLoading: true }, props.idOperacion).then((value: ComunicacionesOperacionModel) => {
    Object.assign(model, value);
  });

  isInitialLoad.value = false;

  const interval = setInterval(function () {
    if (document.hidden) {
      return;
    }
    refrescarInfoChat();
  }, 20000); // 20 segundos

  scrollToLastMessage();

  onUnmounted(() => {
    clearInterval(interval);
  });
});

onUnmounted(() => {
  clearInterval(interval);
});

watch(
  () => model.Comunicaciones,
  () => {
    scrollToLastMessage();
  },
  { deep: true },
);

const loadDetalleOperacion = async () => {
  switch (props.tipo) {
    case "ENERGIA":
      detalleOperacion.value = await api.callStandardApi("getOperacionEnergiaDetail", { showLoading: true }, props.idOperacion);
      break;
    case "AGUA":
      detalleOperacion.value = await api.callStandardApi("getOperacionAguaDetail", { showLoading: true }, props.idOperacion);
      break;
  }
};

const interval = setInterval(function () {
  if (document.hidden) {
    return;
  }
  refrescarInfoChat();
  leerComunicaciones();
}, 20000); // 20 segundos

const refrescarInfoChat = async () => {
  await api.callStandardApi("getComunicacionesOperacionById", { showLoading: false }, props.idOperacion).then((data) => {
    Object.assign(model, {
      ...model,
      Comunicaciones: data.Comunicaciones,
    });
    leerComunicaciones();
  });
};

const leerComunicaciones = async () => {
  await api.callStandardApi("readComunicacionesOperacion", { showLoading: false }, props.idOperacion);
};

const esBackoffice = computed(() => {
  return autho.hasPermission(PermissionsApp.GetAllTickets) || autho.hasPermission(PermissionsApp.GetAssociatedTickets);
});

const scrollToLastMessage = () => {
  nextTick(() => {
    const lastMessageElement = document.getElementById("last-message");
    if (lastMessageElement) {
      lastMessageElement.scrollIntoView({ behavior: "instant" });
    }
  });
};

const sendMessage = async () => {
  var mensaje = createEmptyMensajeTicketModel();
  mensaje.Contenido = textoMensaje.value;

  if (mensaje.Contenido != "") {
    try {
      await setAdjuntos(mensaje, ficheros.value!);
      await api.callStandardApi("addComunicacionOperacion", { showLoading: true }, model.IdOperacion, mensaje).then(async () => {
        await api.callStandardApi("getComunicacionesOperacionById", { showLoading: false }, props.idOperacion).then((value: ComunicacionesOperacionModel) => {
          Object.assign(model, value);
          limpiarAreaRespuesta();
        });
      });
      return true;
    } catch (e) {
      toastError(intl.i18n.t("chat.detalle.mensaje_fallo"));
      return false;
    }
  } else {
    toastError(intl.i18n.t("chat.detalle.mensaje_vacio"));
    return false;
  }
};

const createEmptyMensajeTicketModel = (): CreateComunicacionOperacionModel => ({
  Contenido: "",
  Formato: "text/plain",
  Adjuntos: [],
});

const limpiarAreaRespuesta = () => {
  textoMensaje.value = "";
  ficheros.value = {};
};

async function setAdjuntos(modalModel: CreateComunicacionOperacionModel, ficheros: Ficheros): Promise<void> {
  for (const key of Object.getOwnPropertyNames(ficheros)) {
    const fichero = ficheros[key];
    if (fichero.status === "new" || fichero.status === "modified") {
      const uploadId = await api.callStandardApi("postDocumentoTemp", { showLoading: true }, fichero.fileName!, fichero.file!);
      modalModel.Adjuntos.push({ Nombre: fichero.fileName!, Url: uploadId });
    } else if (fichero.status === "unchanged") {
      //modalModel.Mensaje.Adjuntos.push({ Nombre: fichero.fileName!, Url: null });  // Por si alguna vez tenemos multiple subida con edicion : de nada por el hint
    }
  }
}

async function urlToBlob(ruta: string) {
  const response = await fetch(ruta);
  const data = await response.blob();
  const blob = new Blob([data], { type: "application/pdf;charset=utf-8" });
  return blob;
}

// Función para descargar adjunto (necesaria para adjuntos en mensajes)
async function descargaAdjunto(ruta: string) {
  try {
    const tempUrl = await api.callStandardApi("documentosGenerateTempUrl", { showLoading: false }, ruta);
    const blob = await urlToBlob(tempUrl);
    const file = ruta.split("/").pop();
    FileSaver.saveAs(blob, file);
  } catch (error) {
    console.error("Error al descargar adjunto:", error);
  }
}

const todosLosAdjuntos = computed(() => {
  const adjuntos: string[] = [];
  model.Comunicaciones?.forEach((mensaje) => {
    if (mensaje.RutasAdjuntos && mensaje.RutasAdjuntos.length > 0) {
      adjuntos.push(...mensaje.RutasAdjuntos);
    }
  });
  return adjuntos;
});

const irADetalleOperacion = () => {
  emit("detalle-operacion");
};

const puedeEscribirNuevoMensaje = computed((): boolean => {
  if (props.esBackoffice) return detalleOperacion.value?.UserNameResponsable === auth.userInfo?.username && !operacionFinalizada.value;
  else return !!detalleOperacion.value?.UserNameResponsable && !operacionFinalizada.value;
});

const operacionFinalizada = computed((): boolean => {
  return detalleOperacion.value?.EstaFinalizada ?? false;
});

const textoNoPuedeEscribirMensaje = (esBackoffice: boolean): string => {
  if (esBackoffice)
    return operacionFinalizada.value
      ? intl.i18n.t("chatOperacion.mensaje.placeholder_no_puede_escribir_bo_finalizada")
      : intl.i18n.t("chatOperacion.mensaje.placeholder_no_puede_escribir_bo");
  else
    return operacionFinalizada.value
      ? intl.i18n.t("chatOperacion.mensaje.placeholder_no_puede_escribir_colaborador_finalizada")
      : intl.i18n.t("chatOperacion.mensaje.placeholder_no_puede_escribir_colaborador");
};

const showAutoAsignarResponsable = computed((): boolean => {
  return props.esBackoffice && detalleOperacion.value?.UserNameResponsable != null && detalleOperacion.value?.UserNameResponsable !== auth.userInfo?.username;
});

const showAsignarResponsable = computed((): boolean => {
  return props.esBackoffice && detalleOperacion.value?.UserNameResponsable === auth.userInfo?.username;
});

const autoAsignarResponsable = async (): Promise<void> => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("chatOperacion.detalle.responsable.mensaje_confirmacion"),
    intl.i18n.t("chatOperacion.detalle.responsable.mensaje_confirmacion_titulo"),
    "okCancel",
    "warning",
  );
  if (respuesta !== messageBoxResult.ok) return;

  try {
    await api.callStandardApi("selfAssignResponsableOperacion", { showLoading: true }, props.idOperacion);
    await loadDetalleOperacion();
  } catch (error) {
    toastError(intl.i18n.t("chatOperacion.detalle.responsable.mensaje_error_cambio"));
  }
};

const asignarResponsable = async (): Promise<void> => {
  let dataSource = await api.callStandardApi("getResponsablesSelectorByIdEmpresaAgentes", { showLoading: false }, detalleOperacion.value?.IdEmpresaAgentes!);

  let idResponsable = (await modal.showModal(
    InputSelectBox,
    {
      label: intl.i18n.t("operacion.detalle.responsable_selector_label"),
      required: true,
      dataSource: dataSource,
    },
    {
      title: intl.i18n.t("operacion.detalle.responsable_selector"),
      width: 450,
      height: 270,
    },
  )) as InputSelectBoxModalResult;

  if (!idResponsable?.value) return;

  if (idResponsable?.result === messageBoxResult.ok) {
    await api
      .callStandardApi("assignResponsableOperacion", { showLoading: true }, props.idOperacion, idResponsable.value)
      .then(() => {
        toastSuccess(intl.i18n.t("operacion.detalle.responsable_mensaje_cambio_exito"));
        loadDetalleOperacion();
        refrescarInfoChat();
      })
      .catch(() => toastError(intl.i18n.t("operacion.detalle.responsable_mensaje_cambio_error")));
  }
};
</script>

<style scoped lang="scss">

.chat-layout {
  display: flex;
  height: calc(100vh - 200px);
}

h6 {
  font-size: 1.25em !important;
}

.chat-main {
  flex: 1;
  display: flex;
  max-height: 100%;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid var(--border-default-default);
  gap: 24px;
}

.chat-messages {
  width: 95%;
  margin: 24px auto;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 8px;
    background-color: var(--grid-alt-row-background);
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--background-primary-default);
    border-radius: 10px;
  }
}

.message {
  display: flex;
  margin-bottom: 24px;
  max-width: 100%;
}

.user-message {
  justify-content: flex-end;
}

.reply-message {
  justify-content: flex-start;
}

.message-content {
  background: var(--background-normal-light);
  padding: 10px;
  border-radius: 10px;
  max-width: 50%;
  min-width: 30%;
  white-space: pre-wrap;
}

.message-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.chat-reply {
  width: 95%;
  margin: 20px auto 0;
  display: flex;
  //align-items: center;
  background: var(--background-normal-light);
  padding: 12px 12px 24px;
  border-radius: 8px;
  max-height: 500px;

  &.con-adjuntos {
    flex-direction: column;
  }
}

.reply-input {
  display: flex;
  flex-grow: 1;
  /* Permite que crezca ocupando el espacio disponible */
  flex-direction: row;
  margin-top: 10px;
  align-items: center;
}

.reply-input textarea {
  flex-grow: 1;
  /* Hace que crezca cuando el contenedor se expanda */
  resize: none;
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 15px;
  min-height: 80px;
  /* Evita que sea demasiado pequeño */
  max-height: 300px;
  /* Opcional, para evitar crecimiento excesivo */
}

.send-button {
  background-color: var(--background-primary-default);
  color: var(--text-primary-on-action);
  border: none;
  padding: 10px;
  margin-left: 20px;
  margin-right: 20px;
  width: 50px;
  min-width: 50px;
  max-height: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 24px;
}

hr {
  margin: 0 !important;
}

.circle-icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--background-secondary-default);
  color: var(--text-secondary-on-action);
  font-size: 25px;
  margin: 5px;
}

.circle-icon-system {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--background-primary-default);
  color: var(--text-primary-on-action);
  font-size: 16px;
  margin: 5px;
}

.circle-icon i {
  font-size: 16px;
}

.reply-attach-file {
  margin-right: 12px;
  flex-shrink: 0;
  /* Evita que este elemento se reduzca */
  padding: 0 12px;
  display: flex;
  flex-wrap: wrap;
  /* Permite múltiples líneas de adjuntos */
  //gap: 10px;
  gap: 4px;
}

.documento {
  cursor: pointer;
  margin-top: 8px;
}

.message-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.message-status {
  display: flex;
  align-items: center;
  margin-left: 8px;
}

.check-single {
  color: var(--icon-primary-default);
  font-size: 12px;
}

.check-double {
  color: var(--text-tertiary-default);
  font-size: 12px;
}

.chat-sidebar {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.sidebar-box {
  border: 1px solid var(--border-default-default);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.sidebar-box h6 {
  font-weight: 600;
  font-size: 1em;
}

@media (max-width: 768px) {
  .chat-layout {
    height: auto;
  }

  .chat-main {
    max-width: 100%;
    width: 100%;
  }

  .chat-sidebar {
    margin-bottom: 20px;
  }

  .chat-container {
    padding: 0 0 4px;
  }

  .chat-header {
    align-items: start;
    flex-wrap: wrap;
  }

  .chat-messages {
    width: 100%;
  }

  .message-content {
    min-width: calc(100% - 50px);
  }

  .chat-reply {
    flex-direction: column;
    width: 100%;
  }

  .send-button {
    margin: 0 4px 0 12px;
  }

  .chat-header .chat-header-right {
    flex-wrap: wrap;
  }
}
</style>
